import { Country, Disaster, User } from './';

export interface DisasterSpecificSurveyInput {
  /* meta data */
  lastUpdatedDate: number;
  lastUpdatedUser: User;

  organizationId: number;
  organizationName: string;

  disaster: Disaster;

  surveySubmissionId: number;

  outdated: boolean;
  organizationRespondingToDisaster: boolean;

  /* responses */

  /* step 1 */

  leadContact: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: {
      firstName: string;
      lastName: string;
      title: string;
      phone1: string;
      email: string;
    };
  };

  /* step 2 */

  hqAddress: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: {
      address1: string;
      address2: string;
      city: string;
      country: Country;
      state: string;
      zip: string;
    };
  };

  /* step 3 */
  reliefRecoveryService: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: {
      isCurrentlyProvidingService: boolean;
      isNotProvidingService: boolean;
      startServiceDate: string;
      stopServiceDate: string;
    };
  };

  activityHistory: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: string;
  };

  disasterStageOfWork: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: {
      mitigationStage: boolean;
      preparedStage: boolean;
      recoveryStage: boolean;
      reliefStage: boolean;
      responseStage: boolean;
    };
  };

  stakeholder: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: string;
  };

  orgResponseVideoUrl: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: string;
  };

  /* step 4 */

  solicitDonation: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: {
      isCurrentlySolicitingDonation: boolean;
      isNotSolicitingDonation: boolean;
      startSolicitDonationDate: string;
      stopSolicitDonationDate: string;
    };
  };

  raisedFund: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: {
      hasValue: boolean;
      fund: number;
      currency: string;
    };
  };

  spentFund: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: {
      hasValue: boolean;
      fund: number;
      currency: string;
    };
  };

  earnedInterest: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: {
      hasValue: boolean;
      fund: number;
      currency: string;
    };
  };

  earnedInterestSpent: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: string;
  };

  transferredFund: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: {
      hasValue: boolean;
      fileId: number;
      fundTransferList: {
        fund: number;
        currency: string;
        receivingOrganization: string;
      }[];
    };
  };

  /* step 5 */
  serviceList: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: {
      directProportion: number;
      indirectProportion: number;
      serviceProvided: {
        serviceId: number;
        name: string;
      };
    }[];
  };

  /* step 6 */

  localStaff: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: {
      mgmtStaffCount: number;
      professionalStaffCount: number;
      supportStaffCount: number;
    };
  };

  nonLocalStaff: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: {
      mgmtStaffCount: number;
      professionalStaffCount: number;
      supportStaffCount: number;
    };
  };

  volunteerStaff: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: {
      mgmtStaffCount: number;
      professionalStaffCount: number;
      supportStaffCount: number;
    };
  };

  activityReport: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: {
      hasValue: boolean;
      reportFrequencyId: number;
      reportFrequencyOther: string;
      webLink: string;
      fileId: number;
    };
  };

  engagementList: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: string[];
  };

  additionalInfo: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: string;
  };

  organizationDto: {
    country: string;
  };

  /* Step 7 */
  locationList: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: [
      {
        geoLatitude: number;
        geoLongitude: number;
        radius: number;
      }
    ];
  };

  donateButtonDisabled: boolean;
}

export interface DisasterSpecificSurveyOutput {
  /* meta data */

  previousSurveyId: number;

  /* responses */

  /* step 1 */

  leadContact: {
    markAsUpdated: boolean;

    value: {
      firstName: string;
      lastName: string;
      title: string;
      phone1: string;
      email: string;
    };
  };

  /* step 2 */

  hqAddress: {
    markAsUpdated: boolean;

    value: {
      address1: string;
      address2: string;
      addressId: number;
      city: string;
      country: {
        countryId: number;
      };
      state: string;
      zip: string;
    };
  };

  /* step 3 */
  reliefRecoveryService: {
    markAsUpdated: boolean;

    value: {
      isCurrentlyProvidingService: boolean;
      isNotProvidingService: boolean;
      startServiceDate: string;
      stopServiceDate: string;
    };
  };

  activityHistory: {
    markAsUpdated: boolean;

    value: string;
  };

  disasterStageOfWork: {
    markAsUpdated: boolean;

    value: {
      mitigationStage: boolean;
      preparedStage: boolean;
      recoveryStage: boolean;
      reliefStage: boolean;
      responseStage: boolean;
    };
  };

  stakeholder: {
    markAsUpdated: boolean;

    value: string;
  };

  orgResponseVideoUrl: {
    markAsUpdated: boolean;

    value: string;
  };

  /* step 4 */

  solicitDonation: {
    markAsUpdated: boolean;

    value: {
      isCurrentlySolicitingDonation: boolean;
      isNotSolicitingDonation: boolean;
      startSolicitDonationDate: string;
      stopSolicitDonationDate: string;
    };
  };

  raisedFund: {
    markAsUpdated: boolean;

    value: {
      hasValue: boolean;
      fund: number;
      currency: string;
    };
  };

  spentFund: {
    markAsUpdated: boolean;

    value: {
      hasValue: boolean;
      fund: number;
      currency: string;
    };
  };

  earnedInterest: {
    markAsUpdated: boolean;

    value: {
      hasValue: boolean;
      fund: number;
      currency: string;
    };
  };

  earnedInterestSpent: {
    markAsUpdated: boolean;

    value: string;
  };

  transferredFund: {
    markAsUpdated: boolean;

    value: {
      hasValue: boolean;
      receivingOrganization: string;
    };
  };

  /* step 5 */
  serviceList: {
    markAsUpdated: boolean;

    value: {
      directProportion: number;
      indirectProportion: number;
      serviceProvided: {
        serviceId: number;
      };
    }[];
  };

  /* step 6 */

  localStaff: {
    markAsUpdated: boolean;

    value: {
      mgmtStaffCount: number;
      professionalStaffCount: number;
      supportStaffCount: number;
    };
  };

  nonLocalStaff: {
    markAsUpdated: boolean;

    value: {
      mgmtStaffCount: number;
      professionalStaffCount: number;
      supportStaffCount: number;
    };
  };

  volunteerStaff: {
    markAsUpdated: boolean;

    value: {
      mgmtStaffCount: number;
      professionalStaffCount: number;
      supportStaffCount: number;
    };
  };

  activityReport: {
    markAsUpdated: boolean;

    value: {
      hasValue: boolean;
      reportFrequencyId: number;
      reportFrequencyOther: string;
      webLink: string;
      fileId: number;
    };
  };

  engagementList: {
    markAsUpdated: boolean;

    value: string[];
  };

  additionalInfo: {
    markAsUpdated: boolean;

    value: string;
  };

  /* Step 7 */
  locationList: {
    markAsUpdated: boolean;

    value: [
      {
        geoLatitude: number;
        geoLongitude: number;
        radius: number;
      }
    ];
  };
}

export interface DisasterSpecificSurveyServiceListValue {
  directProportion: number;

  indirectProportion: number;

  serviceProvided: {
    serviceId: number;
    name: string;
  };
}
